/**
 * Student nationality and residential address helpers (#574). State of origin,
 * LGA and the permanent home address are school custom fields now, added from
 * a template in Settings → Custom Fields, so they are not core fields here.
 */

import type { Student } from '@/types/composition-functions';

/** Suggestions for the editable nationality picker; any other nationality can still be typed. */
export const NATIONALITY_SUGGESTIONS: readonly string[] = [
  'Nigerian', 'Ghanaian', 'Beninese', 'Togolese', 'Cameroonian', 'Nigerien', 'Chadian', 'British', 'American',
];

/** The nationality and residential address fields a student record carries. */
export type StudentOriginFields = Partial<Pick<Student, 'nationality' | 'home_address' | 'state' | 'city'>>;

export const ORIGIN_FIELD_KEYS = ['nationality', 'home_address', 'state', 'city'] as const;

/** One labelled nationality/address row on a profile. */
export interface OriginDisplayRow {
  key: typeof ORIGIN_FIELD_KEYS[number];
  label: string;
  value: string;
  /** Addresses are long: layouts give these rows the full width. */
  wide: boolean;
}

/**
 * Display rows for a profile, labelled so the residential address is never
 * mistaken for a permanent one. `emptyText` is what a blank field shows
 * (profiles differ: "N/A" on the admin profile, "-" on the portals).
 */
export function originDisplayRows(fields: StudentOriginFields, options: { emptyText?: string } = {}): OriginDisplayRow[] {
  const emptyText = options.emptyText ?? 'N/A';
  const show = (v: string | null | undefined) => (v && v.trim() !== '' ? v : emptyText);
  const row = (key: OriginDisplayRow['key'], label: string, wide = false): OriginDisplayRow => ({
    key, label, value: show(fields[key]), wide,
  });
  return [
    row('nationality', 'Nationality'),
    row('home_address', 'Residential Address', true),
    row('state', 'State of Residence'),
    row('city', 'City'),
  ];
}

/**
 * Answer or column names about where a student is FROM (state of origin, LGA,
 * permanent home address). Those are custom fields now (#574), so the loose
 * "name contains" matching of admission answers and import columns must never
 * hand them to a residential column ("State" would otherwise take "State of
 * Origin"). Case, spaces, underscores and punctuation are ignored.
 */
const ORIGIN_NAMES = new Set([
  'stateoforigin', 'originstate', 'lga', 'lgaoforigin', 'localgovernment', 'localgovernmentarea',
  'permanentaddress', 'permanenthomeaddress', 'hometownaddress',
]);

export function isOriginAnswerName(name: string): boolean {
  return ORIGIN_NAMES.has(name.toLowerCase().replace(/[^a-z0-9]/g, ''));
}

/** Nationality and address fields copied from loosely typed data (edit prefill), blanks as ''. */
export function pickOriginFields(data: Record<string, unknown> | null | undefined): Required<{ [K in typeof ORIGIN_FIELD_KEYS[number]]: string }> {
  const read = (k: string) => (typeof data?.[k] === 'string' ? (data[k] as string) : '');
  return {
    nationality: read('nationality'),
    home_address: read('home_address'),
    state: read('state'),
    city: read('city'),
  };
}
